import React, {useContext, useEffect, useState} from "react";
import Loading from "../../common/Loading";
import ErrorView from "../../common/ErrorView";
import {apiContext} from "../../ApiContext";
import SelectAccountsContainer from "./select-accounts/SelectAccountsContainer";
import FeedConfirmationResultContainer from "./feed-confirmation/openbanking/FeedConfirmationResultContainer";
import {FullStory} from "@fullstory/browser";
import * as _ from 'lodash';
import * as PropTypes from "prop-types";
import {endSpan, setSpanStatus, startSpan} from "../../common/telemetry/OpenTelemetryHelper";
import SpanStatusCode from "../../common/telemetry/SpanStatusCode";
import {safeTrack} from "../../common/telemetry/SegmentHelper";
import ErrorViewV2 from "../../common/ErrorViewV2";
import helpUrls, {formatUrl} from "../../HelpUrls";
import {getDecodedValue} from "../../common/utils";
import {getOnboardingState, setOnboardingState} from "../../common/OnboardingSessionStorage";
import {OnboardingState} from "../../common/OnboardingState";

function OnboardFinaliseContainer({authCode, consentId, onboardingApplicationData, error}) {
  const [isLoading, setIsLoading] = useState(true)
  const [errorType, setErrorType] = useState(null)
  const [feedAccountDetails, setFeedAccountDetails] = useState()
  const [accountsToOnboard, setAccountsToOnboard] = useState([])
  const [isFeedUpdated, setIsFeedUpdated] = useState(false)
  const api = useContext(apiContext)

  const accountsAlreadyOnboarded = (accounts) => {
    return _.filter(accounts, account => account.onboarded);
  }

  const areAllAccountsUnsupported = (accounts) => {
    return _.every(accounts, account => account.unsupportedDescription);
  }

  const getAccountsNotOnboarded = (accounts) => {
    return _.filter(accounts, account => !account.onboarded && !account.unsupportedDescription);
  }

  const getConsumerId = () => {
    return getDecodedValue("consumerId", onboardingApplicationData)
  }

  const getApplicationId = () => {
    return getDecodedValue("applicationId", onboardingApplicationData)
  }

  const getSource = () => {
    return getDecodedValue("source", onboardingApplicationData)
  }

  const getBankName = () => {
    return getDecodedValue("bankName", onboardingApplicationData) || null;
  }

  const isFromOpenBanking = () => {
    return getSource()?.toLowerCase() === "openbanking";
  }

  const isForSmep = () => {
    return getConsumerId()?.toLowerCase() === "smep";
  }

  const revokeConsentForOpenBanking = async (accountData) => {
    try {
      isFromOpenBanking() && consentId && await api.revokeConsent(getApplicationId(), consentId);
      isFromOpenBanking() && await safeTrack(
        'elementClicked', {
          action: 'clicked_cancel_account_selection',
          page: 'onboard/finalise',
          businessId: accountData.consumptionId,
          source: 'OpenBanking',
          consumer: accountData.consumerId,
          applicationId: getApplicationId()
        }
      )
      isFromOpenBanking() && isForSmep() && await safeTrack(
        'CancelFromChooseAccountsPage', {
          page: 'onboard/finalise',
          businessId: accountData.consumptionId,
          source: 'OpenBanking',
          consumer: 'SMEP',
          applicationId: getApplicationId(),
          productFamily: 'SME',
          label: 'bank-feeds-portal',
          url: window.location.href
        }
      )
      setOnboardingState(getApplicationId(), OnboardingState.FINALISED)
    } catch {
      setErrorType('openBankingFinaliseError');
    }
  }

  const sendEventForCreateFeeds = (consumptionId, consumerId, isAutoSubmitted) => {
    const eventName = isAutoSubmitted ? 'elementViewed' : 'elementClicked';
    safeTrack(
      eventName, {
        action: 'created_feeds_successfully',
        page: 'onboard/finalise',
        businessId: consumptionId,
        source: getSource(),
        consumer: consumerId,
        applicationId: getApplicationId()
      })
  }

  const createFeeds = async (selectedAccounts, accountData, isAutoSubmitted) => {
    setAccountsToOnboard(selectedAccounts);

    const accountsToExtend = isFromOpenBanking() ? accountsAlreadyOnboarded(accountData.accounts) : [];
    const accountsToOnboardOrExtend = [...selectedAccounts, ...accountsToExtend]
    if (_.isEmpty(accountsToOnboardOrExtend)) {
      setOnboardingState(getApplicationId(), OnboardingState.FINALISED)
      return;
    }

    setIsLoading(true);
    try {
      await api.createFeeds(getApplicationId(), accountsToOnboardOrExtend);
      setIsFeedUpdated(true);
      sendEventForCreateFeeds(accountData.consumptionId, accountData.consumerId, isAutoSubmitted);
      setOnboardingState(getApplicationId(), OnboardingState.FEED_CREATED)
    } catch (err) {
      if (isFromOpenBanking()) {
        isForSmep() && safeTrack(
          'FailedToCreateOpenBankingBankFeed', {
            page: 'onboard/finalise',
            businessId: accountData.consumptionId,
            source: 'OpenBanking',
            consumer: 'SMEP',
            applicationId: getApplicationId(),
            productFamily: 'SME',
            label: 'bank-feeds-portal',
            url: window.location.href
          }
        )
        setErrorType('openBankingFinaliseError');
      } else {
        err.status === 409 ? setErrorType('applicationCannotProceed') : setErrorType('finalisationFailed');
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    let loadInOpenBankingFinaliseSpan = undefined
    if (isFromOpenBanking()) {
      loadInOpenBankingFinaliseSpan = startSpan('load_in_fetching_open_banking_account_detail_span');
    }
    const getFeedAccountDetails = async () => {
      let accountData;
      try {
        accountData = await api.finaliseAndFetchAccountData(authCode, onboardingApplicationData, consentId);
        if (isFromOpenBanking()) {
          setSpanStatus(loadInOpenBankingFinaliseSpan, {code: SpanStatusCode.OK});
          endSpan(loadInOpenBankingFinaliseSpan);
        }
        setFeedAccountDetails(accountData);
      } catch (err) {
        if (isFromOpenBanking()) {
          setSpanStatus(loadInOpenBankingFinaliseSpan, {code: SpanStatusCode.ERROR});
          endSpan(loadInOpenBankingFinaliseSpan);
          isForSmep() && safeTrack(
            'LandOnBankFeedPortalErrorPage', {
              page: 'onboard/finalise',
              businessId: accountData?.consumptionId,
              source: 'OpenBanking',
              consumer: 'SMEP',
              applicationId: getApplicationId(),
              productFamily: 'SME',
              label: 'bank-feeds-portal',
              url: window.location.href
            }
          )
          setErrorType('openBankingFinaliseError');
          return;
        }
        if (err.status === 409) {
          setErrorType('applicationCannotProceed');
        } else setErrorType('applicationFailed');
        return
      }
      if (_.isEmpty(accountData.accounts)) {
        setErrorType('noAccountsError');
        return;
      }

      isFromOpenBanking() && sendEventForUnsupportedAccounts(accountData);

      if (isFromOpenBanking()) {
        const accountsNotOnboarded = getAccountsNotOnboarded(accountData.accounts);
        if (accountData.availableFeeds === null || accountsNotOnboarded.length <= accountData.availableFeeds) {
          await createFeeds(accountsNotOnboarded, accountData, true);
          setSpanStatus(loadInOpenBankingFinaliseSpan, {code: SpanStatusCode.OK});
          endSpan(loadInOpenBankingFinaliseSpan);
        }
      }
      setIsLoading(false)
    };

    const sendEventForUnsupportedAccounts = (accountData) => {
      const unsupportedDescriptions = _.chain(accountData.accounts)
        .filter(account => account.unsupportedDescription)
        .map('unsupportedDescription')
        .uniq()
        .value();

      unsupportedDescriptions.forEach(unsupportedDescription => {
        safeTrack('elementViewed', {
          action: unsupportedDescription,
          page: 'onboard/finalise',
          businessId: accountData.consumptionId,
          source: 'OpenBanking',
          consumer: accountData.consumerId,
          applicationId: getApplicationId()
        });
      });
    }

    const declineApplication = async () => {
      try {
        await api.updateApplicationStatus(getApplicationId(), "ConsentDeclined");
        setErrorType('accountsNotConnected');
      } catch {
        setErrorType('applicationFailed');
      }
    }

    error ? declineApplication() : getFeedAccountDetails();
  }, []);

  const parseUrl = (url) => {
    try {
      new URL(url.trim());
      return url
    } catch (e) {
      return null
    }
  }

  const redirectUrl = parseUrl(getDecodedValue("productRedirectUrl", onboardingApplicationData))

  const renderError = () => {
    FullStory('trackEvent', {
      name: 'Onboard Finalise Error',
      properties: {
        error_code: errorType,
        source: getSource()
      },
    })

    if(getConsumerId() === 'Solo')  return <ErrorView type={errorType} redirectUrl={redirectUrl}/>
    const onboardingState = getOnboardingState(getApplicationId());
    const myobbErrorType = onboardingState === OnboardingState.FINALISED ? 'openBankingFinalisedRetryError' :
      onboardingState === OnboardingState.FEED_CREATED ? 'openBankingOnboardedRetryError' : errorType;
    return <ErrorViewV2 type={myobbErrorType} redirectUrl={redirectUrl} redirectToHelpPage={redirectToHelpPage} />
  }

  const redirectToHelpPage = (productPage) => {
    const url = formatUrl(helpUrls.GetHelpSettingUpBankUrl, getConsumerId(), productPage, getBankName())
    window.open(url, '_blank').focus()
  }

  const prepareAccountsForOnboarding = (accounts) => {
    return isFromOpenBanking() ? _.sortBy(accounts, function (account) {
        return [account.onboarded, account.unsupportedDescription];
      }) :
      getAccountsNotOnboarded(accounts);
  }

  const renderFeedsOnboardingView = () => {
    const isReadyToShowConfirmation = isFeedUpdated ||
      (isFromOpenBanking() ? areAllAccountsUnsupported(feedAccountDetails.accounts) : _.isEmpty(getAccountsNotOnboarded(feedAccountDetails.accounts)));
    return (isReadyToShowConfirmation) ?
      <FeedConfirmationResultContainer
        source={getSource()}
        accounts={feedAccountDetails.accounts}
        selectedOnboardAccounts={accountsToOnboard}
        redirectUrl={redirectUrl}
        redirectToHelpPage={redirectToHelpPage}
        consumerId={getConsumerId()}
      /> :
      <SelectAccountsContainer
        accounts={prepareAccountsForOnboarding(feedAccountDetails.accounts)}
        bankfeedLimit={feedAccountDetails.bankfeedLimit}
        availableFeeds={feedAccountDetails.availableFeeds}
        productRedirectUrl={redirectUrl}
        subscriptionUpgradeUrl={feedAccountDetails.subscriptionUpgradeUrl}
        onSubmit={(selectedAccounts) => createFeeds(selectedAccounts, feedAccountDetails, false)}
        onCancel={() => revokeConsentForOpenBanking(feedAccountDetails)}
        source={getSource()}
        redirectToHelpPage={redirectToHelpPage}
        setErrorType={setErrorType}
        consumerId={getConsumerId()}
        page="OnboardFinalise"
      />
  }

  return (
    <div>
      {
        errorType ? renderError() :
          isLoading ?
            <Loading message={"Fetching your feed info..."}/> :
            renderFeedsOnboardingView()
      }
    </div>
  )
}

OnboardFinaliseContainer.propTypes = {
  authCode: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  consentId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onboardingApplicationData: PropTypes.string.isRequired,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};

export default OnboardFinaliseContainer;
